import { useUserData } from "./useUserData";
import { Release, ReleasePricingInfo, ReleasePricingType } from "../types";

export function useReleasePricingInfo(release?: Release): ReleasePricingInfo {
  const { userData } = useUserData();
  const userIsGold = userData?.gold ?? false;

  if (!release) {
    return {
      type: ReleasePricingType.Free,
      isUnlocked: true,
    };
  }

  const {
    ppb_price_point,
    ppb_gold_price_point,
    ppb_sale_price_point,
    ppb_gold_sale_price_point,
    release_user_purchase,
    is_free,
    premium_only,
  } = release;

  // Convert prices from cents to dollars
  const regularPrice = ppb_price_point ? ppb_price_point / 100 : undefined;
  const goldPrice = ppb_gold_price_point
    ? parseFloat((ppb_gold_price_point / 100).toFixed(2))
    : undefined;
  const salePrice = ppb_sale_price_point
    ? parseFloat((ppb_sale_price_point / 100).toFixed(2))
    : undefined;
  const goldSalePrice = ppb_gold_sale_price_point
    ? parseFloat((ppb_gold_sale_price_point / 100).toFixed(2))
    : undefined;

  // Handle purchased releases
  if (release_user_purchase) {
    return {
      type: ReleasePricingType.Purchased,
      isUnlocked: true,
    };
  }

  // Handle free releases
  if (is_free) {
    return {
      type: ReleasePricingType.Free,
      isUnlocked: true,
    };
  }

  // Handle non-premium releases (ALC system)
  if (!premium_only) {
    // If user is gold, they get it free
    if (userIsGold) {
      return {
        type: ReleasePricingType.UnlockedWithGold,
        isUnlocked: true,
      };
    }

    // Non-gold user sees pricing options (but doesn't have access)
    if (!regularPrice) {
      // No price set, just free with gold
      return {
        type: ReleasePricingType.FreeWithGold,
        isUnlocked: false,
      };
    }

    // Has price and potentially sale price
    if (salePrice) {
      return {
        type: ReleasePricingType.OnSaleOrFreeWithGold,
        regularPrice,
        salePrice,
        isOnSale: true,
        isUnlocked: false,
        pricePointToUse: ppb_sale_price_point,
      };
    }

    return {
      type: ReleasePricingType.PpbOrFreeWithGold,
      regularPrice,
      isUnlocked: false,
      pricePointToUse: ppb_price_point,
    };
  }

  // Handle premium-only releases
  const hasGoldPrice = !!goldPrice;
  const hasRegularSale = !!salePrice;
  const hasGoldSale = !!goldSalePrice;

  const hasAnySale = hasRegularSale || hasGoldSale;

  if (!hasAnySale) {
    if (hasGoldPrice && regularPrice !== goldPrice) {
      return {
        type: userIsGold
          ? ReleasePricingType.GoldDiscount
          : ReleasePricingType.AvailableGoldDiscount,
        regularPrice,
        goldPrice,
        isUnlocked: false,
        pricePointToUse: userIsGold ? ppb_gold_price_point : ppb_price_point,
      };
    }

    return {
      type: ReleasePricingType.Regular,
      regularPrice,
      isUnlocked: false,
      pricePointToUse: ppb_price_point,
    };
  }

  const hasBothSales = hasRegularSale && hasGoldSale;
  const hasRegularSaleOnly = hasRegularSale && !hasGoldSale;
  const hasGoldSaleOnly = !hasRegularSale && hasGoldSale;

  if (hasBothSales) {
    return {
      type: userIsGold
        ? ReleasePricingType.OnGoldSale
        : ReleasePricingType.onSaleWithAvailableOnGoldSale,
      regularPrice,
      goldPrice,
      salePrice,
      goldSalePrice,
      hasGoldDiscount: true,
      isOnSale: true,
      isUnlocked: false,
      pricePointToUse: userIsGold
        ? ppb_gold_sale_price_point
        : ppb_sale_price_point,
    };
  }

  if (hasRegularSaleOnly) {
    if (hasGoldPrice) {
      return {
        type: userIsGold
          ? ReleasePricingType.OnSaleWithGoldDiscount
          : ReleasePricingType.OnSaleAndAvailableGoldDiscount,
        regularPrice,
        salePrice,
        goldPrice,
        hasGoldDiscount: true,
        isOnSale: true,
        isUnlocked: false,
        pricePointToUse: ppb_sale_price_point,
      };
    }

    return {
      type: ReleasePricingType.OnSale,
      regularPrice,
      salePrice,
      isOnSale: true,
      isUnlocked: false,
      pricePointToUse: ppb_sale_price_point,
    };
  }

  if (hasGoldSaleOnly) {
    return {
      type: userIsGold
        ? ReleasePricingType.OnGoldSale
        : ReleasePricingType.RegularWithAvailableOnGoldSale,
      regularPrice,
      goldPrice,
      goldSalePrice,
      hasGoldDiscount: true,
      isOnSale: userIsGold,
      isUnlocked: false,
      pricePointToUse: userIsGold ? ppb_gold_sale_price_point : ppb_price_point,
    };
  }

  return { type: ReleasePricingType.Free, isUnlocked: true };
}
